/**
 * Content-aware crops with smartcrop.js (vendored, global `smartcrop`), boosted by the faces found
 * by curator.FaceFinder so nobody gets their head cut off.
 *
 * crop() works on the small canvas kept by the curator and answers a rectangle normalized to the
 * picture, for the wanted aspect ratio. minScale 1 means the crop is as large as the ratio allows :
 * smartcrop only decides where to place it, never zooms in.
 */
fx.ns("curator", class SmartCropper {

    constructor(options)
    {
        options = options || {};

        this.minScale = options.minScale || 1.0;
        this.faceWeight = options.faceWeight || 1.0;
        this.facePadding = options.facePadding || 0.35;  // grows face boxes so hair and chin stay in
    }

    get available()
    {
        return typeof smartcrop != "undefined" && smartcrop && typeof smartcrop.crop == "function";
    }

    /**
     * @param canvas  working canvas of the photo
     * @param ratio   wanted width / height
     * @param faces   normalized faces [{ x, y, width, height }] (optional)
     * @returns Promise<{ x, y, width, height }> normalized crop, null when smartcrop is missing
     */
    crop(canvas, ratio, faces)
    {
        if (!this.available || !ratio || !canvas)
            return Promise.resolve(null);

        let cw = canvas.width, ch = canvas.height;
        let width, height;

        if (ratio >= cw / ch)
        {
            width = cw;
            height = cw / ratio;
        }
        else
        {
            height = ch;
            width = ch * ratio;
        }

        width = Math.max(1, Math.floor(width));
        height = Math.max(1, Math.floor(height));

        let boost = (faces || []).map((face) => this._boost(face, cw, ch));

        return smartcrop.crop(canvas, { width : width, height : height, minScale : this.minScale, boost : boost, ruleOfThirds : true })
            .then((result) => {
                let c = result.topCrop;
                let crop = { x : c.x / cw, y : c.y / ch, width : c.width / cw, height : c.height / ch };
                return this._keepFaces(crop, faces);
            });
    }

    // ------------------------------------------------------------------ //

    _boost(face, cw, ch)
    {
        let padX = face.width * this.facePadding;
        let padY = face.height * this.facePadding;

        let x = Math.max(0, face.x - padX);
        let y = Math.max(0, face.y - padY * 1.5);   // more room above : hair, hats
        let right = Math.min(1, face.x + face.width + padX);
        let bottom = Math.min(1, face.y + face.height + padY);

        return {
            x : Math.round(x * cw),
            y : Math.round(y * ch),
            width : Math.max(1, Math.round((right - x) * cw)),
            height : Math.max(1, Math.round((bottom - y) * ch)),
            weight : this.faceWeight
        };
    }

    /**
     * When the winning crop still cuts through the important faces, slide it (same size) so they fit.
     * If they cannot fit at that size, smartcrop's choice is kept.
     */
    _keepFaces(crop, faces)
    {
        if (!faces || !faces.length)
            return crop;

        let important = faces.filter((face) => face.width * face.height >= 0.004);
        if (!important.length)
            important = [faces[0]];

        let left = 1, top = 1, right = 0, bottom = 0;
        for (let i = 0; i < important.length; i++)
        {
            let face = important[i];
            left = Math.min(left, face.x);
            top = Math.min(top, face.y);
            right = Math.max(right, face.x + face.width);
            bottom = Math.max(bottom, face.y + face.height);
        }

        if (right - left > crop.width || bottom - top > crop.height)
            return crop;

        let x = crop.x, y = crop.y;

        if (left < x)
            x = left;
        else if (right > x + crop.width)
            x = right - crop.width;

        if (top < y)
            y = top;
        else if (bottom > y + crop.height)
            y = bottom - crop.height;

        x = Math.max(0, Math.min(1 - crop.width, x));
        y = Math.max(0, Math.min(1 - crop.height, y));

        return { x : x, y : y, width : crop.width, height : crop.height };
    }
});
